import { useContext, useEffect, useRef, useState } from 'react';
import { DayOfWeek, defaultCalendarStrings, Calendar } from '@fluentui/react';
import {
  CalendarStyleProps,
  MonthCalendarContainer,
  MonthLabel,
} from './styles/MonthCalendar.styled';
import { createPortal } from 'react-dom';
import { vacationIndicatorData } from './utils/findVacationIndicatorTarget';
import { requestDatesRange } from './utils/requestDatesRange';
import { TeamsFxContext } from 'src/context/TeamsContext';
import { MonthSelector } from './mobile/MonthSelector';
import { TeamVacationData } from 'src/tabs/hooks/useGetTeamRequests';
import { VacationsRequest } from '../RequestsWidget/models/RequestWidget.interface';
import { currentUserVacationRequestMapper } from 'src/tabs/utils/mappers/currentUserVacationRequestMapper';
import { VacationType } from 'src/tabs/hooks/useVacationTypes';
import { MobileCalendar } from './mobile/MobileCalendar';
import { EmployeeVacationDays } from '../../../../tabs/models/SummaryInterfaces';
import { TeamVacationCircle } from '../../TeamVacationCircle/TeamVacationCircle';
import {
  generateReactKey,
  KeyRoots,
} from 'src/utils/generateUniqueReactKey/generateUniqueReactKey';

interface MonthCalendarProps {
  month: number;
  monthString: string;
  firstDayOfMonth: Date;
  lastDayOfMonth: Date;
  weekends: Date[];
  year: number;
  incrementMonth?: () => void;
  decrementMonth?: () => void;
  requests: Array<TeamVacationData>;
  currentUserRequests: VacationsRequest[];
  vacationTypes: VacationType[];
  hideEmployees: boolean;
  vacationPeriodNextYear: EmployeeVacationDays;
  vacationPeriodPrevYear: EmployeeVacationDays;
}

export const MonthCalendar = ({
  month,
  monthString,
  firstDayOfMonth,
  lastDayOfMonth,
  weekends,
  requests,
  year,
  incrementMonth,
  decrementMonth,
  currentUserRequests,
  vacationTypes,
  hideEmployees,
  vacationPeriodNextYear,
  vacationPeriodPrevYear,
}: MonthCalendarProps) => {
  const { isMobile } = useContext(TeamsFxContext);
  const [teamVacationComp, setTeamVacationComp] = useState([]);
  const calendarRef = useRef(null);
  const mappedCurrentUserRequests = currentUserVacationRequestMapper(
    currentUserRequests,
    vacationTypes
  );
  useEffect(() => {
    const requestsRange = !hideEmployees ? requestDatesRange(requests) : [];
    const currentUserRequestsRange = requestDatesRange(
      mappedCurrentUserRequests
    );

    const combinedRequestsRange = [
      ...requestsRange,
      ...currentUserRequestsRange,
    ];
    const singleCalendarNode =
      calendarRef.current &&
      calendarRef.current.childNodes[1].childNodes[1].childNodes[0]
        .childNodes[0];

    const loadingDependancies =
      singleCalendarNode &&
      (singleCalendarNode.childNodes || isMobile) &&
      (requests.length > 0 || currentUserRequests.length > 0);
    const teamVaccationComp =
      loadingDependancies &&
      vacationIndicatorData(
        singleCalendarNode.childNodes,
        combinedRequestsRange
      );

    setTeamVacationComp(teamVaccationComp);
  }, [requests, currentUserRequests, year]);

  return (
    <MonthCalendarContainer index={month} key={month} selectedMonth={false}>
      {!isMobile && <MonthLabel>{monthString}</MonthLabel>}
      {isMobile && (
        <>
          <MonthSelector
            month={monthString}
            year={year}
            incrementMonth={incrementMonth}
            decrementMonth={decrementMonth}
            vacationPeriodNextYear={vacationPeriodNextYear}
            vacationPeriodPrevYear={vacationPeriodPrevYear}
          />
          <MobileCalendar
            month={month}
            year={year}
            restrictedDates={weekends}
            requests={requests}
            mappedCurrentUserRequests={mappedCurrentUserRequests}
            hideEmployees={hideEmployees}
          />
        </>
      )}
      {!isMobile && (
        <>
          {teamVacationComp.length > 0 &&
            teamVacationComp.map((comp, index) => {
              return createPortal(
                <TeamVacationCircle
                  key={generateReactKey(
                    KeyRoots.TIME_OFF_TEAM_VACATION_COMP,
                    index
                  )}
                  month={month}
                  employees={comp.data}
                  employeesOverflow={comp.data}
                  date={comp.date}
                  target={
                    comp.target?.parentNode ||
                    comp.currentUserIndicatorData.target?.parentNode
                  }
                  currentUser={comp.currentUserIndicatorData}
                />,
                comp.target || comp.currentUserIndicatorData.target
              );
            })}
          <Calendar
            allFocusable={false}
            calendarDayProps={{ allFocusable: false }}
            value={firstDayOfMonth}
            firstDayOfWeek={DayOfWeek.Monday}
            showMonthPickerAsOverlay={false}
            isMonthPickerVisible={false}
            showGoToToday={false}
            strings={defaultCalendarStrings}
            styles={CalendarStyleProps}
            maxDate={lastDayOfMonth}
            ref={calendarRef}
            minDate={firstDayOfMonth}
            restrictedDates={weekends}
            today={new Date(null)}
          />
        </>
      )}
    </MonthCalendarContainer>
  );
};
